import { useCallback, useEffect, useRef } from "react";
import MapBrowserEvent from "ol/MapBrowserEvent";
import Map from "ol/Map";
import { FeatureCollection, FeatureFromGeojson } from "../../FeaturesStore";
import { getMapAppState } from "../../utils/mapState";

interface UseFeatureClickSelectionOptions {
  map: Map;
  geoJson: FeatureCollection;
  selectedFeature: FeatureFromGeojson | null | undefined;
  onSelect: ((value: FeatureFromGeojson | null) => void) | undefined;
  changeGeoJson: (geoJson: FeatureCollection) => void;
  unSelectFeature: () => void;
}

/**
 * 지도 클릭 → 피처 탐색 → GeoJSON 선택 상태 업데이트 파이프라인을 처리합니다.
 * 드로잉/수정 중에는 선택을 무시합니다.
 */
export function useFeatureClickSelection({
  map,
  geoJson,
  selectedFeature,
  onSelect,
  changeGeoJson,
  unSelectFeature,
}: UseFeatureClickSelectionOptions) {
  // 클릭 핸들러를 안정화하기 위해 최신 값을 ref로 유지
  const stateRef = useRef({ geoJson, selectedFeature, onSelect, changeGeoJson, unSelectFeature });
  stateRef.current = { geoJson, selectedFeature, onSelect, changeGeoJson, unSelectFeature };

  const handleClick = useCallback(
    (e: MapBrowserEvent<any>) => {
      const { isDrawing, isModifying } = getMapAppState(map);
      if (isDrawing || isModifying) return;

      const { geoJson: currentGeoJson, selectedFeature: currentSelected, onSelect: currentOnSelect, changeGeoJson: currentChangeGeoJson, unSelectFeature: currentUnselect } = stateRef.current;

      // 겹쳐있는 피처 위에서부터 선택되도록 리버스
      const reversedFeatures = map
        .getFeaturesAtPixel(e.pixel, { hitTolerance: 10 })
        .reverse();

      for (const feature of reversedFeatures) {
        const featureId = feature.getId();
        if (!featureId) continue;

        const target = currentGeoJson.features.find((item) => item.id === featureId);
        if (!target) continue;

        currentOnSelect?.(target);

        const isAlreadySelected = currentSelected?.id === target.id;
        const updatedGeoJson: FeatureCollection = {
          ...currentGeoJson,
          features: currentGeoJson.features.map((item) => ({
            ...item,
            properties: {
              ...item.properties,
              isSelected: item.id === target.id ? !isAlreadySelected : false,
            },
          })),
        };
        currentChangeGeoJson(updatedGeoJson);

        if (isAlreadySelected) {
          currentUnselect();
        }
        // 겹친 피처 중 최상위만 선택
        break;
      }
    },
    [map]
  );

  useEffect(() => {
    map.on("click", handleClick);
    return () => map.un("click", handleClick);
  }, [map, handleClick]);
}
